import { useEffect, useMemo } from 'react';

import { useAccount } from 'wagmi';

import SkeletonCard, { GridStatsCard } from '@/components/shared/GridStatsCard';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { fetchLoanStatistics } from '@/redux/loans/loansSlice';
import { USDDollarFormatter } from '@/utils/formatter';

const LoanStats: React.FC = () => {
  const loanStatistics = useAppSelector((state) => state.loans.loanStatistics);
  const loanStatisticsLoading = useAppSelector((state) => state.loans.loanStatisticsLoading);
  const dispatch = useAppDispatch();
  const { address } = useAccount();
  const stats = useMemo(
    () => [
      {
        name: 'Active Loans',
        value: loanStatistics?.activeCount || 0,
      },
      {
        name: 'Repaid Loans',
        value: loanStatistics?.repaidCount || 0,
      },
      {
        name: 'Defaulted Loans',
        value: loanStatistics?.foreclosedCount || 0,
      },
      {
        name: 'Repayment Rate',
        value: `${(loanStatistics?.repaymentRate || 0).toFixed(2)}%`,
      },
      {
        name: 'Borrowed',
        value: USDDollarFormatter(loanStatistics?.borrowed || 0),
        price: true,
      },
      {
        name: 'Interest Paid',
        value: USDDollarFormatter(loanStatistics?.interestPaidAmount || 0),
        price: true,
        valueClassName: '!text-rose',
      },
    ],
    [loanStatistics]
  );

  useEffect(() => {
    if (address) {
      dispatch(fetchLoanStatistics({ address }));
    }
  }, [dispatch, address]);

  return (
    <div className='flex flex-col gap-7 lg:flex-row justify-between items-center pb-7 lg:p-7 pt-0 lg:pt-0 max-lg:px-6 border-b'>
      <div className='grid grid-cols-6 w-max'>
        {loanStatisticsLoading
          ? new Array(6).fill(null).map((_, idx) => {
              return <SkeletonCard key={idx} />;
            })
          : stats.map(({ name, value, price, valueClassName }) => <GridStatsCard key={name} title={name} value={value} price={price} className='min-h-[91px]' valueClassName={valueClassName} />)}
      </div>
    </div>
  );
};

export default LoanStats;
